import api from './api';

export interface ReportEnvelope<T> {
  reportName: string;
  generatedAt: string;
  dateRange: { start: string; end: string };
  data: T;
}

export interface SalesSummaryTypeInfo {
  count: number;
  revenue: number;
}

export interface SalesSummaryData {
  totalOrders: number;
  totalRevenue: number;
  avgOrderValue: number;
  byType: Record<string, SalesSummaryTypeInfo>;
}

export interface ClinicRevenueItem {
  clinicId: string;
  clinicName: string;
  orderCount: number;
  revenue: number;
  avgValue: number;
  outstandingBalance: number;
}

// API returns data as a flat array of ClinicRevenueItem
export type ClinicRevenueData = ClinicRevenueItem[];

export interface AgentCommissionItem {
  agentId: string;
  agentName: string;
  totalCommission: number;
  pendingCommission: number;
  paidCommission: number;
  rentalCount: number;
  clinicCount: number;
}

// API returns data as a flat array of AgentCommissionItem
export type AgentCommissionData = AgentCommissionItem[];

export interface AgedReceivableItem {
  label: string;
  count: number;
  amount: number;
}

export interface AgedReceivablesData {
  buckets: AgedReceivableItem[];
  total: { count: number; amount: number };
}

export interface InventoryUsageItem {
  sku: string;
  productName: string;
  totalUnits: number;
  available: number;
  inUse: number;
  utilizationRate: number;
  dispatchesInPeriod: number;
}

// API returns data as a flat array of InventoryUsageItem
export type InventoryUsageData = InventoryUsageItem[];

export interface ConsignmentItem {
  rentalId: string;
  orderNumber: string;
  clinicName: string;
  patientName: string;
  productName: string;
  startDate: string;
  endDate: string;
  weeks: number;
  amount: number;
  status: string;
}

// API returns data as a flat array of ConsignmentItem
export type ConsignmentData = ConsignmentItem[];

export type DateRangeParams = { startDate?: string; endDate?: string };

export interface OverdueItem {
  rentalId: string;
  orderNumber: string | null;
  customerName: string | null;
  clinicName: string | null;
  clinicId: string | null;
  productName: string | null;
  productSku: string | null;
  serialNumbers: string[];
  startDate: string | null;
  endDate: string | null;
  weeks: number;
  daysOverdue: number;
  amount: number;
  status: string;
  orderType: string;
}

export interface OverdueReportData {
  reportName: string;
  generatedAt: string;
  filter: string;
  summary: {
    totalOverdue: number;
    totalValue: number;
    avgDaysOverdue: number;
  };
  data: OverdueItem[];
}

export interface FulfilledByClinicItem {
  rentalId: string;
  orderNumber: string | null;
  customerName: string | null;
  clinicName: string | null;
  clinicId: string | null;
  productName: string | null;
  startDate: string | null;
  endDate: string | null;
  amount: number;
  status: string;
}

export interface FulfilledByClinicReport {
  reportName: string;
  generatedAt: string;
  dateRange: { start: string; end: string };
  summary: {
    totalOrders: number;
    totalRevenue: number;
    clinicBreakdown: Array<{ name: string; count: number; revenue: number }>;
  };
  data: FulfilledByClinicItem[];
}

export interface BillingReportItem {
  rentalId: string;
  orderNumber: string | null;
  createdAt: string;
  clinicName: string | null;
  clinicId: string | null;
  orderType: string;
  customerName: string | null;
  startDate: string | null;
  endDate: string | null;
  subtotal: number;
  tax: number;
  shipping: number;
  total: number;
  status: string;
  invoiceNumber: string | null;
  invoiceStatus: string | null;
}

export interface BillingReport {
  reportName: string;
  generatedAt: string;
  dateRange: { start: string; end: string };
  summary: { totalOrders: number; totalRevenue: number };
  data: BillingReportItem[];
}

export interface MissingSendDateItem {
  rentalId: string;
  orderNumber: string | null;
  customerName: string | null;
  clinicName: string | null;
  clinicId: string | null;
  productName: string | null;
  startDate: string;
  daysUntilStart: number;
  status: string;
}

export interface MissingSendDatesReport {
  reportName: string;
  generatedAt: string;
  filter: { withinDays: number };
  summary: { totalAtRisk: number; pastStart: number };
  data: MissingSendDateItem[];
}

export interface SentOrderItem {
  rentalId: string;
  orderNumber: string | null;
  shipDate: string;
  carrier: string | null;
  serviceName: string | null;
  trackingNumber: string | null;
  clinicName: string | null;
  clinicId: string | null;
  customerName: string | null;
  products: string[];
}

export interface SentOrdersReport {
  reportName: string;
  generatedAt: string;
  dateRange: { start: string; end: string };
  summary: { totalSent: number };
  data: SentOrderItem[];
}

/**
 * Download a report as CSV through the authenticated client (a plain <a href>
 * would not carry the App Bridge session token). The blob-URL anchor pattern
 * mirrors the shipping-label download in WarehouseDispatch.
 */
async function downloadReportCsv(
  path: string,
  params: Record<string, string | number | undefined> = {},
): Promise<void> {
  const response = await api.get(path, {
    params: { ...params, format: 'csv' },
    responseType: 'blob',
  });

  const disposition = response.headers['content-disposition'] as string | undefined;
  const match = disposition?.match(/filename="?([^";]+)"?/);
  const filename =
    match?.[1] || `report-${new Date().toISOString().split('T')[0]}.csv`;

  const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export const reportsApi = {
  getSalesSummary: async (params: DateRangeParams = {}) => {
    const response = await api.get<ReportEnvelope<SalesSummaryData>>('/reports/sales-summary', { params });
    return response.data;
  },

  getClinicRevenue: async (params: DateRangeParams & { clinicId?: string } = {}) => {
    const response = await api.get<ReportEnvelope<ClinicRevenueData>>('/reports/clinic-revenue', { params });
    return response.data;
  },

  getAgentCommission: async (params: DateRangeParams = {}) => {
    const response = await api.get<ReportEnvelope<AgentCommissionData>>('/reports/agent-commission', { params });
    return response.data;
  },

  getAgedReceivables: async (params: DateRangeParams = {}) => {
    const response = await api.get<ReportEnvelope<AgedReceivablesData>>('/reports/aged-receivables', { params });
    return response.data;
  },

  getInventoryUsage: async (params: DateRangeParams = {}) => {
    const response = await api.get<ReportEnvelope<InventoryUsageData>>('/reports/inventory-usage', { params });
    return response.data;
  },

  getConsignment: async (params: DateRangeParams = {}) => {
    const response = await api.get<ReportEnvelope<ConsignmentData>>('/reports/consignment', { params });
    return response.data;
  },

  /**
   * Dates are optional and there is no default range: undated means every overdue
   * rental. The API deliberately does not fall back to the last 30 days here, which
   * would hide the longest-overdue rentals — the ones that matter most.
   */
  getOverdue: async (params: DateRangeParams & { type?: string } = {}) => {
    const query: Record<string, string> = {};
    if (params.type) query.type = params.type;
    if (params.startDate) query.startDate = params.startDate;
    if (params.endDate) query.endDate = params.endDate;
    const response = await api.get<OverdueReportData>('/reports/overdue', { params: query });
    return response.data;
  },

  getFulfilledByClinic: async (params: DateRangeParams = {}) => {
    const response = await api.get<FulfilledByClinicReport>('/reports/fulfilled-by-clinic', { params });
    return response.data;
  },

  getPatient: async (params: DateRangeParams = {}) => {
    const response = await api.get('/reports/patient', { params });
    return response.data;
  },

  getSerial: async (params: DateRangeParams = {}) => {
    const response = await api.get('/reports/serial', { params });
    return response.data;
  },

  getProduct: async (params: DateRangeParams = {}) => {
    const response = await api.get('/reports/product', { params });
    return response.data;
  },

  getBilling: async (params: DateRangeParams & { clinicId?: string } = {}) => {
    const response = await api.get<BillingReport>('/reports/billing', { params });
    return response.data;
  },

  getMissingSendDates: async (params: { days?: number } = {}) => {
    const response = await api.get<MissingSendDatesReport>('/reports/missing-send-dates', { params });
    return response.data;
  },

  getSentOrders: async (params: DateRangeParams = {}) => {
    const response = await api.get<SentOrdersReport>('/reports/sent-orders', { params });
    return response.data;
  },

  downloadCsv: downloadReportCsv,
};

export default reportsApi;
